import { FC, ReactNode, useState } from 'react';
import { Dialog } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../components/Typography';
import { MatchOverview, TeamInfo } from '../../components/MatchCard';
import { ChevronUp, ChevronDown, CloseRounded, Injured } from '../../components/svgs';

// ─── Shared types ─────────────────────────────────────────────────────────────

type PriceTrend = 'up' | 'down' | 'flat';

export interface PlayerHeroData {
  firstName: string;
  lastName: string;
  position: string;
  price?: string;
  priceChange?: string;
  priceTrend?: PriceTrend;
  teamLabel: string;
  shirt: ReactNode;
  teamFlag: ReactNode;
  injuryDetails: {
    type: string | null;
    startDate: string | null;
    endDate: string | null;
    expectedEndDate: string | null;
    status: string | null;
  } | null;
}

const HeaderCloseButton: FC<{ onClose?: () => void; label?: string }> = ({
  onClose,
  label = 'Close',
}) => (
  <button
    type="button"
    onClick={onClose}
    className="absolute right-4 top-4 inline-flex size-6 items-center justify-center rounded-[4px] text-[#222526] hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
    aria-label={label}
  >
    <CloseRounded width={40} height={40} circleColor="transparent" crossColor="#222526" />
  </button>
);

const PlayerHero: FC<{
  data: PlayerHeroData;
  largeName?: boolean;
  onClose?: () => void;
}> = ({ data, onClose }) => {
  const showPriceChange = !!data.priceChange && data.priceTrend !== 'flat';

  return (
    <div className="relative flex w-full items-stretch">
      <div className="relative flex h-[100px] w-[88px] shrink-0 items-center justify-center overflow-hidden bg-[#27a550]">
        {data.shirt}
      </div>

      <div className="flex flex-1 flex-col gap-[2px] bg-[--page-header-bg-grey] px-[12px] pb-[12px] pt-[16px]">
        <Typography className="text-[14px] leading-[20px] text-[--color-neutral-900]">
          {data.firstName}
        </Typography>
        <div className="flex items-center gap-[12px]">
          <Typography className="text-[14px] leading-[20px] font-bold text-[--color-neutral-900]">
            {data.lastName}
          </Typography>
          <span className="rounded-[4px] bg-white px-[8px] py-[2px]">
            <Typography variant="subheadline010" className="text-[14px] leading-[18px]">
              {data.position}
            </Typography>
          </span>
          {Boolean(data.injuryDetails) && <Injured className="h-[16px] w-[16px]" />}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[8px]">
            <Typography variant="subheadline030" className="text-[18px] leading-[24px]">
              {data.price}
            </Typography>
            {showPriceChange ? (
              <Typography
                variant="paragraph000"
                className="text-[12px] leading-[16px] text-[--color-neutral-700]"
              >
                {data.priceChange}
              </Typography>
            ) : null}
          </div>
          <div className="flex items-center gap-[4px]">
            <Typography variant="paragraph000" className="text-[12px] leading-[16px] ">
              {data.teamLabel}
            </Typography>
            <div className="size-6 overflow-hidden rounded-full border border-white">
              {data.teamFlag}
            </div>
          </div>
        </div>
      </div>

      <HeaderCloseButton onClose={onClose} />
    </div>
  );
};

// ─── Profile card ─────────────────────────────────────────────────────────────

export interface ProfileSummaryItem {
  label: string;
  value: string;
}

export interface NextFixtureItem {
  isHome: boolean;
  label: string;
  opponentName: string;
  opponentFlag: ReactNode;
}

export interface PlayerProfileCardProps {
  hero: PlayerHeroData;
  showCaptainActions?: boolean;
  captainLabel?: string;
  viceCaptainLabel?: string;
  activeCaptainAction?: 'captain' | 'viceCaptain' | null;
  captainActionLoading?: boolean;
  viceCaptainActionLoading?: boolean;
  captainActionDisabled?: boolean;
  viceCaptainActionDisabled?: boolean;
  summary: ProfileSummaryItem[];
  nextFixturesTitle?: string;
  nextFixtures: NextFixtureItem[];
  primaryActionLabel?: string;
  primaryActionIcon?: ReactNode;
  primaryActionDisabled?: boolean;
  primaryActionTitle?: string;
  hidePrimaryAction?: boolean;
  secondaryActionLabel?: string;
  onClose?: () => void;
  onCaptainAction?: () => void;
  onViceCaptainAction?: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  className?: string;
}

const ActionButton: FC<{
  label: string;
  icon?: ReactNode;
  kind?: 'primary' | 'outline' | 'purple-outline' | 'purple-solid';
  loading?: boolean;
  disabled?: boolean;
  title?: string;
  onClick?: () => void;
}> = ({ label, icon, kind = 'outline', loading = false, disabled = false, title, onClick }) => (
  <button
    type="button"
    disabled={disabled || loading}
    title={title}
    onClick={onClick}
    className={twMerge(
      'inline-flex h-12 min-w-px flex-1 items-center justify-center gap-2 rounded-[4px] px-6 py-3',
      disabled
        ? 'cursor-not-allowed border border-[#f3f3f3] bg-[#f3f3f3] text-[#bebfbf]'
        : kind === 'primary'
          ? 'bg-[#0072ee] text-white'
          : kind === 'purple-solid'
            ? 'bg-[#a036e0] text-white'
            : kind === 'purple-outline'
              ? 'border border-[#a036e0] text-[#a036e0]'
              : 'border border-[#0072ee] text-[#0072ee]'
    )}
  >
    <Typography
      as="span"
      variant="button020"
      className={twMerge(
        disabled
          ? 'text-[#bebfbf]'
          : kind === 'primary'
            ? 'text-white'
            : kind === 'purple-solid'
              ? 'text-white'
              : kind === 'purple-outline'
                ? 'text-[#a036e0]'
                : 'text-[#0072ee]'
      )}
    >
      {loading ? 'Loading...' : label}
    </Typography>
    {loading ? undefined : icon}
  </button>
);

export const PlayerProfileCard: FC<PlayerProfileCardProps> = ({
  hero,
  showCaptainActions = true,
  captainLabel = 'Captain',
  viceCaptainLabel = 'Vice Captain',
  activeCaptainAction = null,
  captainActionLoading = false,
  viceCaptainActionLoading = false,
  captainActionDisabled = false,
  viceCaptainActionDisabled = false,
  summary,
  nextFixturesTitle = 'NEXT FIXTURES',
  nextFixtures,
  primaryActionLabel = 'Transfer Out',
  primaryActionIcon,
  primaryActionDisabled = false,
  primaryActionTitle,
  hidePrimaryAction = false,
  secondaryActionLabel,
  onClose,
  onCaptainAction,
  onViceCaptainAction,
  onPrimaryAction,
  onSecondaryAction,
  className,
}) => {
  return (
    <article
      className={twMerge(
        'w-full bg-white shadow-[0px_4px_40px_0px_rgba(18,18,18,0.16)] overflow-hidden rounded-[12px]',
        className
      )}
      id="player-profile-card"
    >
      <PlayerHero data={hero} onClose={onClose} />

      <div className="flex flex-col gap-4 p-4">
        {showCaptainActions && (
          <div className="flex items-center gap-4">
            <ActionButton
              label={captainLabel}
              kind={activeCaptainAction === 'captain' ? 'purple-solid' : 'purple-outline'}
              loading={captainActionLoading}
              disabled={captainActionDisabled}
              onClick={onCaptainAction}
              icon={
                activeCaptainAction === 'captain' ? (
                  <Typography as="span" variant="button020" className="text-white">
                    C
                  </Typography>
                ) : undefined
              }
            />
            <ActionButton
              label={viceCaptainLabel}
              kind={activeCaptainAction === 'viceCaptain' ? 'purple-solid' : 'purple-outline'}
              loading={viceCaptainActionLoading}
              disabled={viceCaptainActionDisabled}
              onClick={onViceCaptainAction}
              icon={
                activeCaptainAction === 'viceCaptain' ? (
                  <Typography as="span" variant="button020" className="text-white">
                    VC
                  </Typography>
                ) : undefined
              }
            />
          </div>
        )}

        <div className="flex items-center gap-1">
          {summary.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className="flex min-w-px flex-1 flex-col items-center gap-1"
            >
              <Typography
                as="p"
                variant="paragraph010"
                className="text-[10px] leading-[10px] text-[#222526] text-center"
              >
                {item.label}
              </Typography>
              <Typography as="p" variant="subheadline040" className="text-[#222526] text-center">
                {item.value}
              </Typography>
            </div>
          ))}
        </div>

        <section className="flex flex-col items-center gap-3 rounded-[12px] bg-[#eae9fd] px-4 pb-4 pt-3">
          <Typography as="h3" variant="label040" className="text-[#222526] !font-[800]">
            {nextFixturesTitle}
          </Typography>
          <div className="flex w-full items-center gap-3">
            {nextFixtures.map((fixture, index) => (
              <div
                key={`${fixture.label}-${index}`}
                className="flex min-w-px flex-1 flex-col items-center justify-center gap-2 rounded-[4px] bg-white px-1 py-2"
              >
                <Typography
                  as="p"
                  variant="paragraph010"
                  className="text-[#222526] text-[10px] text-center truncate w-full"
                >
                  {fixture.label}
                </Typography>
                <div className="size-8">{fixture.opponentFlag}</div>
                <Typography
                  as="p"
                  variant="subheadline010"
                  className={twMerge(
                    'text-[#222526] text-center truncate w-full',
                    fixture.isHome ? 'uppercase' : 'lowercase'
                  )}
                >
                  {fixture.opponentName}
                </Typography>
              </div>
            ))}
          </div>
        </section>

        <div className="flex items-center gap-4">
          {!hidePrimaryAction && (
            <ActionButton
              label={primaryActionLabel}
              icon={primaryActionIcon}
              kind="primary"
              disabled={primaryActionDisabled}
              title={primaryActionTitle}
              onClick={onPrimaryAction}
            />
          )}
          {secondaryActionLabel && (
            <ActionButton label={secondaryActionLabel} kind="outline" onClick={onSecondaryAction} />
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Match card ───────────────────────────────────────────────────────────────

export interface PlayerMatchStat {
  label: string;
  total: string | number;
  points: string | number;
  icon?: ReactNode;
  onIconClick?: () => void;
  details?: Array<{ label: string; total: string | number; points?: string | number }>;
}

export interface PlayerMatchCardProps {
  hero: PlayerHeroData;
  competitionLabel: string;
  competitionIcon?: ReactNode;
  mdLabel?: string;
  mdPoints: string;
  leftTeam: TeamInfo;
  rightTeam: TeamInfo;
  status?: string;
  statusVariant?: 'finished' | 'live';
  stats: PlayerMatchStat[];
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  onClose?: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  className?: string;
}

export const PlayerMatchCard: FC<PlayerMatchCardProps> = ({
  hero,
  competitionLabel,
  competitionIcon,
  mdLabel = 'MD 1 Total:',
  mdPoints,
  leftTeam,
  rightTeam,
  status = 'FT',
  statusVariant = 'finished',
  stats,
  primaryActionLabel = 'Full Profile',
  secondaryActionLabel,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  return (
    <article
      className={twMerge(
        'w-full bg-white shadow-[0px_4px_40px_0px_rgba(18,18,18,0.16)] overflow-hidden rounded-[12px]'
      )}
    >
      <PlayerHero data={hero} largeName onClose={onClose} />

      <div className="flex flex-col gap-4 p-4">
        <div className="flex w-full items-center justify-between">
          <div className="rounded-[4px] bg-[--page-header-bg-grey] px-[8px] py-[4px]">
            {competitionIcon}
            <Typography variant="paragraph000" className="text-[12px]">
              {competitionLabel}
            </Typography>
          </div>

          <div className="flex items-center gap-[8px]">
            <Typography variant="paragraph000" className="text-[12px]">
              {mdLabel}
            </Typography>
            <span className="rounded-[4px] bg-[#0072ee] px-[8px] py-[2px]">
              <Typography variant="subheadline020" className="text-[14px] font-bold text-white">
                {mdPoints} PTS
              </Typography>
            </span>
          </div>
        </div>

        <MatchOverview
          leftTeam={leftTeam}
          rightTeam={rightTeam}
          status={status}
          statusVariant={statusVariant}
          className="w-full rounded-[4px] bg-[#eae9fd] px-2 py-3"
        />

        <div className="w-full">
          <div className="flex items-center gap-[16px] border-b border-[--color-neutral-200] pb-[12px]">
            <Typography
              variant="paragraph000"
              className="flex-1 text-[--color-neutral-600] text-[10px] leading-[10px]"
            >
              STAT
            </Typography>
            <Typography
              variant="paragraph000"
              className="w-[40px] text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
            >
              TOTAL
            </Typography>
            <Typography
              variant="paragraph000"
              className="w-[40px] text-center text-[--color-neutral-600] text-[10px] leading-[10px]"
            >
              POINTS
            </Typography>
          </div>

          {stats.map((row, index) => {
            const isExpanded = expandedRows.has(index);
            const hasDetails = !!row.details?.length;
            return (
              <div key={`${row.label}-${index}`} className="border-b border-[#eae9fd] py-3">
                <div className="flex w-full items-center gap-4">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Typography
                      as="span"
                      variant="subheadline010"
                      className="truncate text-[#222526]"
                    >
                      {row.label}
                    </Typography>
                    {hasDetails && (
                      <button
                        type="button"
                        onClick={() => {
                          const next = new Set(expandedRows);
                          if (next.has(index)) {
                            next.delete(index);
                          } else {
                            next.add(index);
                          }
                          setExpandedRows(next);
                          row.onIconClick?.();
                        }}
                        className="inline-flex items-center justify-center rounded-sm p-0.5 hover:bg-[#eae9fd]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#030033]"
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                      >
                        {isExpanded ? (
                          <ChevronUp className="size-5 text-[#222526]" />
                        ) : (
                          <ChevronDown className="size-5 text-[#222526]" />
                        )}
                      </button>
                    )}
                    {!hasDetails && row.icon}
                  </div>
                  <Typography
                    as="span"
                    variant="subheadline020"
                    className="w-10 shrink-0 text-center text-[#222526]"
                  >
                    {row.total}
                  </Typography>
                  <Typography
                    as="span"
                    variant="subheadline020"
                    className="w-10 shrink-0 text-center text-[#0072ee]"
                  >
                    {row.points}
                  </Typography>
                </div>

                {isExpanded && !!row.details?.length && (
                  <div className="mt-3 flex flex-col gap-2">
                    {row.details.map((detail, detailIndex) => (
                      <div
                        key={`${detail.label}-${detailIndex}`}
                        className="flex w-full items-center gap-4"
                      >
                        <Typography
                          as="span"
                          variant="paragraph010"
                          className="min-w-0 flex-1 text-[#222526]"
                        >
                          {detail.label}
                        </Typography>
                        <Typography
                          as="span"
                          variant="subheadline010"
                          className="w-10 shrink-0 text-center text-[#222526]"
                        >
                          {detail.total}
                        </Typography>
                        <Typography
                          as="span"
                          variant="subheadline010"
                          className={twMerge(
                            'w-10 shrink-0 text-center',
                            detail.points !== undefined ? 'text-[#0072ee]' : 'text-[#222526]'
                          )}
                        >
                          {detail.points !== undefined ? detail.points : ''}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <ActionButton label={primaryActionLabel} kind="outline" onClick={onPrimaryAction} />
          {secondaryActionLabel && (
            <ActionButton label={secondaryActionLabel} kind="outline" onClick={onSecondaryAction} />
          )}
        </div>
      </div>
    </article>
  );
};

// ─── Modal wrappers ───────────────────────────────────────────────────────────

interface BasePlayerModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
}

const modalContentClassName = twMerge(
  'fixed left-1/2 top-1/2 z-[9999] w-[410px] max-w-[92vw] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
  'focus:outline-none'
);

export interface PlayerProfileModalProps
  extends BasePlayerModalProps, Omit<PlayerProfileCardProps, 'onClose' | 'className'> {}

export const PlayerProfileModal: FC<PlayerProfileModalProps> = ({
  open,
  onOpenChange,
  trigger,
  ...cardProps
}) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[9998] bg-black/50" />
      <Dialog.Content className={modalContentClassName} aria-describedby={undefined}>
        <Dialog.Title className="sr-only">Player Profile</Dialog.Title>
        <PlayerProfileCard {...cardProps} onClose={() => onOpenChange?.(false)} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export interface PlayerMatchModalProps
  extends BasePlayerModalProps, Omit<PlayerMatchCardProps, 'onClose' | 'className'> {}

export const PlayerMatchModal: FC<PlayerMatchModalProps> = ({
  open,
  onOpenChange,
  trigger,
  ...cardProps
}) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
      <Dialog.Content className={modalContentClassName} aria-describedby={undefined}>
        <Dialog.Title className="sr-only">Player Match Card</Dialog.Title>
        <PlayerMatchCard {...cardProps} onClose={() => onOpenChange?.(false)} />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

// ─── Story defaults ───────────────────────────────────────────────────────────

export const defaultProfileSummary: ProfileSummaryItem[] = [
  { label: 'MD Pts', value: '10' },
  { label: 'Avg. Pts', value: '4.8' },
  { label: 'Selected', value: '10.3%' },
  { label: 'Bonus Pts', value: '15' },
  { label: 'Total Pts', value: '190' },
];
